/**
 * chunkedUpload — Client-side helper to upload a File in small chunks.
 * Designed to bypass production ingress body-size / timeout limits which
 * silently drop multi-MB single-shot POSTs.
 *
 * Usage:
 *   await chunkedUpload({
 *     file:        myCSVFile,
 *     fileKey:    'line_items',          // server-side enum
 *     uploadId:   'upl_abc123',          // shared across the 3 invoice CSVs
 *     endpoint:   `${API}/api/super-admin/boyce-import/chunk-upload`,
 *     authHeader: { Authorization: `Bearer ${token}` },
 *     onProgress: ({current, total}) => setPct(current/total),
 *   });
 */
const DEFAULT_CHUNK_BYTES = 2 * 1024 * 1024; // 2 MB — safe under most ingress limits

export async function chunkedUpload({
  file,
  fileKey,
  uploadId,
  endpoint,
  authHeader = {},
  chunkBytes = DEFAULT_CHUNK_BYTES,
  onProgress,
}) {
  if (!file || !(file instanceof Blob)) throw new Error('chunkedUpload: file is required');
  if (!fileKey)  throw new Error('chunkedUpload: fileKey is required');
  if (!uploadId) throw new Error('chunkedUpload: uploadId is required');
  if (!endpoint) throw new Error('chunkedUpload: endpoint is required');

  const total = Math.max(1, Math.ceil(file.size / chunkBytes));

  for (let i = 0; i < total; i++) {
    const start = i * chunkBytes;
    const end   = Math.min(start + chunkBytes, file.size);
    const slice = file.slice(start, end);

    const fd = new FormData();
    fd.append('upload_id',    uploadId);
    fd.append('file_key',     fileKey);
    fd.append('chunk_index',  String(i));
    fd.append('total_chunks', String(total));
    // Preserve filename for server-side logging
    fd.append('chunk', slice, file.name || `${fileKey}.part`);

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: authHeader,
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Chunk ${i + 1}/${total} of ${fileKey} failed: HTTP ${res.status}${text ? ` · ${text.slice(0, 200)}` : ''}`);
    }
    onProgress?.({ fileKey, current: i + 1, total, bytesSent: end });
  }

  return { fileKey, totalChunks: total, sizeBytes: file.size };
}

/** Helper: generate a filesystem-safe upload id. */
export function makeUploadId() {
  // 8 random hex chars + timestamp → 14-20 chars, all [a-z0-9]
  const rnd = Math.random().toString(36).slice(2, 10);
  return `upl_${rnd}_${Date.now().toString(36)}`;
}
